import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { MediaFilesController } from "lib/MediaFileController";
import { AttachedFileData } from "types";

/**
 * View-model for a single message attachment, ready to render.
 *
 * Extends {@link AttachedFileData} (the shape used by MessageAttachView /
 * the composer) with the extra fields the multi-attachment renderer needs:
 * the full file URL and pre-computed type flags.
 */
export interface AttachmentView extends AttachedFileData {
  /** Full file URL (for MediaPreviewModal / download). */
  url: string;
  isImage: boolean;
  isVideo: boolean;
}

/**
 * Maps every file on `message.attachment[]` into a renderable {@link AttachmentView}.
 *
 * Shared by both render sites (MessageBubble — DM/group, MessageDefault —
 * livechat) so multi-attachment messages display identically. Previously each
 * site used `.find()` and showed only the first file.
 *
 * NOTE: `id` is `${subjectId}_${fileNumber}` — unique per attachment. The old
 * single-file code keyed on `fileId.subjectId` alone, which is identical for
 * every file of the same message and would collide as a React key.
 */
export function getAttachmentViews(message: Message): AttachmentView[] {
  const attachments = message.attachment;
  if (!attachments || attachments.length === 0) return [];

  const views: AttachmentView[] = [];
  for (const att of attachments) {
    const file = att?.file;
    if (!file) continue;

    const contentType = file.metadata?.contentType || file.contentType || "";
    const isImage = contentType.startsWith("image");
    const isVideo = contentType.startsWith("video");
    const src = isImage ? MediaFilesController.getImageThumbnail(file.urlPart, "720p") : undefined;

    views.push({
      id: `${file.fileId.subjectId}_${file.fileId.fileNumber}`,
      mediaType: contentType,
      uploadProgress: 100,
      fileName: file.metadata?.filename || "",
      size: Number(file.metadata?.contentLength) || 0,
      src,
      url: MediaFilesController.getFileUrl(file.urlPart),
      isImage,
      isVideo,
    });
  }
  return views;
}
